import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sliders, Waves, Sparkles, Volume2, Power, RotateCcw, Clock, X } from 'lucide-react';
import { AudioEngine } from '../core/audioEngine';
import { PianoEffectsSettings } from '../audio/types';
import { DEFAULT_EFFECTS_SETTINGS } from '../audio/effects/EffectsChain';

interface PianoEffectsPopoverProps {
  audioEngine: AudioEngine;
  className?: string;
}

type TabMode = 'all' | 'reverb' | 'delay' | 'tone-decay';

export const PianoEffectsPopover: React.FC<PianoEffectsPopoverProps> = ({ audioEngine, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabMode>('all');
  const [settings, setSettings] = useState<PianoEffectsSettings>(() => audioEngine.getEffectsSettings());
  
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const [popoverPos, setPopoverPos] = useState<{
    top?: number;
    bottom?: number;
    right: number;
    maxHeight: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    const unsubscribe = audioEngine.onEffectsSettingsChange((newSettings) => {
      setSettings(newSettings);
    });
    return unsubscribe;
  }, [audioEngine]);

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    // Prefer opening downwards if there is at least 320px, or if spaceBelow >= spaceAbove
    const placeBelow = spaceBelow >= 340 || spaceBelow >= spaceAbove;
    const availableHeight = placeBelow ? spaceBelow : spaceAbove;
    const maxHeight = Math.max(260, Math.min(availableHeight - 8, 620));

    const popoverWidth = Math.min(390, window.innerWidth - 24);
    let right = window.innerWidth - rect.right;
    if (right < 12) right = 12;
    if (window.innerWidth - right - popoverWidth < 12) {
      right = Math.max(12, window.innerWidth - popoverWidth - 12);
    }

    setPopoverPos({
      top: placeBelow ? rect.bottom + 6 : undefined,
      bottom: !placeBelow ? window.innerHeight - rect.top + 6 : undefined,
      right,
      maxHeight,
      width: popoverWidth,
    });
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleResizeOrScroll = () => {
        updatePosition();
      };
      window.addEventListener('resize', handleResizeOrScroll, { passive: true });
      window.addEventListener('scroll', handleResizeOrScroll, { passive: true });
      return () => {
        window.removeEventListener('resize', handleResizeOrScroll);
        window.removeEventListener('scroll', handleResizeOrScroll);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        popoverContentRef.current && !popoverContentRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleUpdate = (partial: Partial<PianoEffectsSettings>) => {
    audioEngine.updateEffectsSettings(partial);
  };

  const handleReset = () => {
    audioEngine.updateEffectsSettings(DEFAULT_EFFECTS_SETTINGS);
  };

  const applyPreset = (name: 'dry' | 'studio' | 'hall' | 'cathedral' | 'echo') => {
    switch (name) {
      case 'dry':
        handleUpdate({
          reverb: { enabled: false, mix: 0, roomSize: 1.0, damping: 'natural' },
          delay: { enabled: false, time: 0.32, feedback: 30, mix: 0 },
          tone: { enabled: true, brightness: 0 },
          decayTime: 0.2, // Minimum ultra-sec
        });
        break;
      case 'studio':
        handleUpdate({
          reverb: { enabled: true, mix: 14, roomSize: 0.9, damping: 'warm' },
          delay: { enabled: false, time: 0.24, feedback: 25, mix: 15 },
          tone: { enabled: true, brightness: -10 },
          decayTime: 1.4,
        });
        break;
      case 'hall':
        handleUpdate({
          reverb: { enabled: true, mix: 22, roomSize: 1.8, damping: 'natural' },
          delay: { enabled: false, time: 0.32, feedback: 35, mix: 20 },
          tone: { enabled: true, brightness: 0 },
          decayTime: 1.6,
        });
        break;
      case 'cathedral':
        handleUpdate({
          reverb: { enabled: true, mix: 42, roomSize: 3.2, damping: 'bright' },
          delay: { enabled: false, time: 0.48, feedback: 45, mix: 25 },
          tone: { enabled: true, brightness: 15 },
          decayTime: 2.2,
        });
        break;
      case 'echo':
        handleUpdate({
          reverb: { enabled: true, mix: 20, roomSize: 1.5, damping: 'natural' },
          delay: { enabled: true, time: 0.34, feedback: 42, mix: 28 },
          tone: { enabled: true, brightness: 5 },
          decayTime: 1.8,
        });
        break;
    }
    playAuditionChord();
  };

  const playAuditionChord = (overrideDecay?: number) => {
    const now = audioEngine.getContext().currentTime;
    const decay = overrideDecay ?? settings.decayTime;
    const baseDuration = Math.max(0.15, Math.min(1.5, decay * 0.7));
    audioEngine.scheduleNote(60, 92, now, baseDuration);        // C4
    audioEngine.scheduleNote(64, 88, now + 0.02, baseDuration); // E4
    audioEngine.scheduleNote(67, 85, now + 0.04, baseDuration); // G4
    audioEngine.scheduleNote(71, 90, now + 0.06, baseDuration * 1.1); // B4
    audioEngine.scheduleNote(74, 86, now + 0.08, baseDuration * 1.2); // D5
  };

  const isReverbActive = settings.reverb.enabled && settings.reverb.mix > 0;
  const isDelayActive = settings.delay?.enabled && settings.delay.mix > 0;
  const isAnyFxActive = isReverbActive || isDelayActive;

  return (
    <div className={`relative inline-block text-left ${className}`}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        id="effects-rack-btn"
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        className={`flex items-center gap-1.5 px-2.5 h-7 border text-xs font-semibold transition-all shadow-xs rounded-xs ${
          isAnyFxActive
            ? 'bg-cyan-950/60 border-cyan-400 text-cyan-200 shadow-[0_0_10px_rgba(34,211,238,0.25)]'
            : 'bg-[#101b24] border-cyan-900/60 text-cyan-200/90 hover:bg-[#152330] hover:border-cyan-700'
        }`}
        title="Acoustic Effects & Space Settings"
      >
        <Sliders className={`w-3.5 h-3.5 ${isAnyFxActive ? 'text-cyan-400' : 'text-cyan-400/70'}`} />
        <span className="hidden sm:inline font-bold">FX:</span>
        <span className="font-mono text-[11px] text-cyan-100">
          {isReverbActive && isDelayActive
            ? `${settings.reverb.mix}% Rev + Dly`
            : isReverbActive
            ? `${settings.reverb.mix}% Rev`
            : isDelayActive
            ? `${settings.delay.mix}% Delay`
            : 'Dry'}
        </span>
        {isAnyFxActive && (
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        )}
      </button>

      {/* Popover Rack Panel rendered in Portal to escape any overflow-hidden containers */}
      {isOpen && popoverPos &&
        createPortal(
          <div
            ref={popoverContentRef}
            id="piano-effects-popover-panel"
            style={{
              position: 'fixed',
              top: popoverPos.top !== undefined ? `${popoverPos.top}px` : undefined,
              bottom: popoverPos.bottom !== undefined ? `${popoverPos.bottom}px` : undefined,
              right: `${popoverPos.right}px`,
              width: `${popoverPos.width}px`,
              maxHeight: `${popoverPos.maxHeight}px`,
            }}
            className="flex flex-col bg-neutral-900/98 backdrop-blur-md border border-neutral-700 rounded-xl shadow-2xl z-[99999] text-neutral-200 animate-in fade-in zoom-in-95 duration-100"
          >
            {/* Header (Always Visible) */}
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-neutral-800 shrink-0">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-indigo-950/60 border border-indigo-800/60 text-indigo-400">
                  <Sliders className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-neutral-100 leading-none">
                    Effects & Acoustic Space
                  </h4>
                  <p className="text-[10px] text-neutral-400 mt-0.5">
                    Real-time sound shaping for pianos
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => playAuditionChord()}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-[10px] text-neutral-300 hover:text-white transition-colors"
                  title="Audition chord with current settings"
                >
                  <Volume2 className="w-3 h-3 text-indigo-400" />
                  <span>Audition</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="p-1 rounded text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors"
                  title="Reset to defaults"
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors ml-0.5"
                  title="Close (Esc)"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Quick Presets Bar (Always Visible) */}
            <div className="px-3.5 pt-2.5 pb-1 shrink-0 bg-neutral-950/50 border-b border-neutral-850">
              <div className="flex items-center justify-between text-[9px] uppercase font-bold tracking-wider text-neutral-400 mb-1">
                <span>Acoustic Presets</span>
                <span className="text-[9px] text-neutral-500 font-normal lowercase">min decay: 0.2s</span>
              </div>
              <div className="grid grid-cols-5 gap-1 pb-2">
                <button
                  type="button"
                  onClick={() => applyPreset('dry')}
                  className={`px-1 py-1 rounded text-[10px] font-medium transition-colors ${
                    !settings.reverb.enabled && !settings.delay?.enabled && settings.decayTime <= 0.4
                      ? 'bg-neutral-800 text-white border border-neutral-600'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                  }`}
                  title="Dry sound without effects, 0.2s minimal decay"
                >
                  Dry (0.2s)
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('studio')}
                  className={`px-1 py-1 rounded text-[10px] font-medium transition-colors ${
                    settings.reverb.enabled && !settings.delay?.enabled && settings.reverb.roomSize < 1.2
                      ? 'bg-amber-950/80 text-amber-300 border border-amber-800/80'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                  }`}
                >
                  Studio
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('hall')}
                  className={`px-1 py-1 rounded text-[10px] font-medium transition-colors ${
                    settings.reverb.enabled && !settings.delay?.enabled && settings.reverb.roomSize >= 1.2 && settings.reverb.roomSize <= 2.2
                      ? 'bg-indigo-950/80 text-indigo-300 border border-indigo-800/80'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                  }`}
                >
                  Concert Hall
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('cathedral')}
                  className={`px-1 py-1 rounded text-[10px] font-medium transition-colors ${
                    settings.reverb.enabled && !settings.delay?.enabled && settings.reverb.roomSize > 2.2
                      ? 'bg-cyan-950/80 text-cyan-300 border border-cyan-800/80'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                  }`}
                >
                  Cathedral
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('echo')}
                  className={`px-1 py-1 rounded text-[10px] font-medium transition-colors ${
                    settings.delay?.enabled
                      ? 'bg-sky-950/80 text-sky-300 border border-sky-800/80'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                  }`}
                >
                  Echo + FX
                </button>
              </div>

              {/* View Tabs Filter */}
              <div className="flex items-center gap-1 pt-1 pb-1.5 border-t border-neutral-800 text-[10px]">
                <span className="text-neutral-500 text-[9px] mr-1">View:</span>
                {[
                  { id: 'all', label: 'All (Rack View)' },
                  { id: 'reverb', label: 'Reverb' },
                  { id: 'delay', label: 'Delay' },
                  { id: 'tone-decay', label: 'Decay & Tone' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as TabMode)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-neutral-800 text-indigo-300 border border-neutral-700'
                        : 'text-neutral-400 hover:text-neutral-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable Modules Body */}
            <div className="p-3.5 space-y-3 overflow-y-auto daw-scroll flex-1">
              {/* MODULE: NATURAL DECAY / SUSTAIN */}
              {(activeTab === 'all' || activeTab === 'tone-decay') && (
                <div className="p-2.5 rounded-lg bg-neutral-950/90 border border-neutral-800">
                  <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                    <span className="font-semibold text-neutral-200">Natural Decay & Sustain</span>
                    <span className="font-mono text-indigo-400 font-bold bg-neutral-900 px-1.5 py-0.5 rounded border border-neutral-800 text-xs">
                      {settings.decayTime.toFixed(2)}s
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.2"
                    max="3.0"
                    step="0.05"
                    value={settings.decayTime}
                    onChange={(e) =>
                      handleUpdate({
                        decayTime: Number(e.target.value),
                      })
                    }
                    className="w-full accent-indigo-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                  />
                  {/* Quick Shortcut Buttons */}
                  <div className="flex items-center justify-between gap-1 pt-1.5">
                    {[
                      { label: '0.20s (Dry)', val: 0.20 },
                      { label: '0.60s (Short)', val: 0.60 },
                      { label: '1.40s (Natural)', val: 1.40 },
                      { label: '2.50s (Lush)', val: 2.50 },
                    ].map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => handleUpdate({ decayTime: item.val })}
                        className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${
                          Math.abs(settings.decayTime - item.val) < 0.04
                            ? 'bg-indigo-950 border-indigo-600 text-indigo-300 font-medium'
                            : 'bg-neutral-900 border-neutral-800 text-neutral-400 hover:text-neutral-200'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between text-[9px] text-neutral-500 mt-1">
                    <span>Ultra-dry (0.2s)</span>
                    <span>Standard acoustic</span>
                    <span>Long sustain (3.0s)</span>
                  </div>
                </div>
              )}

              {/* MODULE: REVERBERATION */}
              {(activeTab === 'all' || activeTab === 'reverb') && (
                <div className="p-2.5 rounded-lg bg-neutral-950/90 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Waves className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-xs font-semibold text-neutral-200">
                        Acoustic Reverb
                      </span>
                    </div>
                    {/* On/Off Toggle */}
                    <button
                      type="button"
                      onClick={() =>
                        handleUpdate({
                          reverb: { ...settings.reverb, enabled: !settings.reverb.enabled },
                        })
                      }
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        settings.reverb.enabled
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-neutral-800 text-neutral-400 hover:text-white'
                      }`}
                    >
                      <Power className="w-2.5 h-2.5" />
                      <span>{settings.reverb.enabled ? 'ON' : 'BYPASS'}</span>
                    </button>
                  </div>

                  {/* Mix Slider */}
                  <div className={`space-y-2 transition-opacity ${settings.reverb.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                        <span>Mix (Dry / Wet)</span>
                        <span className="font-mono text-indigo-400 font-bold">{settings.reverb.mix}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={settings.reverb.mix}
                        onChange={(e) =>
                          handleUpdate({
                            reverb: { ...settings.reverb, mix: Number(e.target.value) },
                          })
                        }
                        className="w-full accent-indigo-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                      />
                      <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5">
                        <span>Direct Dry (0%)</span>
                        <span>Balanced (25%)</span>
                        <span>Spacious (100%)</span>
                      </div>
                    </div>

                    {/* Room Size Slider */}
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                        <span>Room Size</span>
                        <span className="font-mono text-neutral-300 font-bold">
                          {settings.reverb.roomSize.toFixed(1)}s
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0.6"
                        max="3.5"
                        step="0.1"
                        value={settings.reverb.roomSize}
                        onChange={(e) =>
                          handleUpdate({
                            reverb: { ...settings.reverb, roomSize: Number(e.target.value) },
                          })
                        }
                        className="w-full accent-indigo-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                      />
                      <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5">
                        <span>Small Room</span>
                        <span>Concert Hall</span>
                        <span>Vast Arena</span>
                      </div>
                    </div>

                    {/* Damping tone selector */}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] text-neutral-400">Damping tone:</span>
                      <div className="flex items-center gap-1 bg-neutral-900 p-0.5 rounded border border-neutral-800 text-[10px]">
                        {(['warm', 'natural', 'bright'] as const).map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            onClick={() =>
                              handleUpdate({
                                reverb: { ...settings.reverb, damping: mode },
                              })
                            }
                            className={`px-1.5 py-0.5 rounded capitalize transition-colors ${
                              settings.reverb.damping === mode
                                ? 'bg-neutral-800 text-indigo-300 font-medium'
                                : 'text-neutral-500 hover:text-neutral-300'
                            }`}
                          >
                            {mode === 'warm' ? 'Warm' : mode === 'natural' ? 'Natural' : 'Bright'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* MODULE: DELAY / ECHO */}
              {(activeTab === 'all' || activeTab === 'delay') && (
                <div className="p-2.5 rounded-lg bg-neutral-950/90 border border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-sky-400" />
                      <span className="text-xs font-semibold text-neutral-200">
                        Stereo Delay & Echo
                      </span>
                    </div>
                    {/* On/Off Toggle */}
                    <button
                      type="button"
                      onClick={() =>
                        handleUpdate({
                          delay: {
                            ...(settings.delay ?? DEFAULT_EFFECTS_SETTINGS.delay),
                            enabled: !(settings.delay?.enabled ?? false),
                          },
                        })
                      }
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        settings.delay?.enabled
                          ? 'bg-sky-600 text-white shadow-xs'
                          : 'bg-neutral-800 text-neutral-400 hover:text-white'
                      }`}
                    >
                      <Power className="w-2.5 h-2.5" />
                      <span>{settings.delay?.enabled ? 'ON' : 'BYPASS'}</span>
                    </button>
                  </div>

                  {/* Delay Controls */}
                  <div className={`space-y-2 transition-opacity ${settings.delay?.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    {/* Mix Slider */}
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                        <span>Mix (Dry / Wet)</span>
                        <span className="font-mono text-sky-400 font-bold">{settings.delay?.mix ?? 0}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={settings.delay?.mix ?? 0}
                        onChange={(e) =>
                          handleUpdate({
                            delay: {
                              ...(settings.delay ?? DEFAULT_EFFECTS_SETTINGS.delay),
                              mix: Number(e.target.value),
                            },
                          })
                        }
                        className="w-full accent-sky-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                      />
                      <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5">
                        <span>Dry (0%)</span>
                        <span>Subtle (20%)</span>
                        <span>Pronounced (50%)</span>
                      </div>
                    </div>

                    {/* Delay Time Slider */}
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                        <span>Delay Time</span>
                        <span className="font-mono text-sky-300 font-bold">
                          {Math.round((settings.delay?.time ?? 0.32) * 1000)} ms
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0.05"
                        max="0.80"
                        step="0.01"
                        value={settings.delay?.time ?? 0.32}
                        onChange={(e) =>
                          handleUpdate({
                            delay: {
                              ...(settings.delay ?? DEFAULT_EFFECTS_SETTINGS.delay),
                              time: Number(e.target.value),
                            },
                          })
                        }
                        className="w-full accent-sky-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                      />
                      {/* Quick Subdivision Presets */}
                      <div className="flex items-center justify-between gap-1 pt-1">
                        {[
                          { label: 'Slapback (120ms)', val: 0.12 },
                          { label: 'Eighth note (320ms)', val: 0.32 },
                          { label: 'Quarter note (500ms)', val: 0.50 },
                        ].map((div) => (
                          <button
                            key={div.label}
                            type="button"
                            onClick={() =>
                              handleUpdate({
                                delay: {
                                  ...(settings.delay ?? DEFAULT_EFFECTS_SETTINGS.delay),
                                  time: div.val,
                                },
                              })
                            }
                            className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${
                              Math.abs((settings.delay?.time ?? 0) - div.val) < 0.02
                                ? 'bg-sky-950 border-sky-600 text-sky-300 font-medium'
                                : 'bg-neutral-900 border-neutral-800 text-neutral-400 hover:text-neutral-200'
                            }`}
                          >
                            {div.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Feedback Slider */}
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                        <span>Feedback & Repeats</span>
                        <span className="font-mono text-neutral-300 font-bold">
                          {settings.delay?.feedback ?? 35}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="80"
                        value={settings.delay?.feedback ?? 35}
                        onChange={(e) =>
                          handleUpdate({
                            delay: {
                              ...(settings.delay ?? DEFAULT_EFFECTS_SETTINGS.delay),
                              feedback: Number(e.target.value),
                            },
                          })
                        }
                        className="w-full accent-sky-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                      />
                      <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5">
                        <span>Single tap</span>
                        <span>Natural trail</span>
                        <span>Long ambient repeats</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* MODULE: TONE & BRIGHTNESS */}
              {(activeTab === 'all' || activeTab === 'tone-decay') && (
                <div className="p-2.5 rounded-lg bg-neutral-950/90 border border-neutral-800">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs font-semibold text-neutral-200">
                        Tone & Brightness
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleUpdate({
                          tone: { ...settings.tone, enabled: !settings.tone.enabled },
                        })
                      }
                      className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        settings.tone.enabled
                          ? 'bg-amber-600 text-white shadow-xs'
                          : 'bg-neutral-800 text-neutral-400 hover:text-white'
                      }`}
                    >
                      <Power className="w-2.5 h-2.5" />
                      <span>{settings.tone.enabled ? 'ON' : 'BYPASS'}</span>
                    </button>
                  </div>

                  <div className={`transition-opacity ${settings.tone.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    <div className="flex items-center justify-between text-[11px] text-neutral-300 mb-1">
                      <span>Color / Brightness</span>
                      <span className="font-mono text-amber-400 font-bold">
                        {settings.tone.brightness > 0
                          ? `+${settings.tone.brightness}`
                          : settings.tone.brightness}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="-50"
                      max="50"
                      value={settings.tone.brightness}
                      onChange={(e) =>
                        handleUpdate({
                          tone: { ...settings.tone, brightness: Number(e.target.value) },
                        })
                      }
                      className="w-full accent-amber-500 h-1.5 bg-neutral-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-[9px] text-neutral-500 mt-0.5">
                      <span>Mellow (Dark)</span>
                      <span>Neutral</span>
                      <span>Bright (Concert)</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer (Always Visible) */}
            <div className="px-3.5 py-2 border-t border-neutral-800 bg-neutral-950/60 flex items-center justify-between text-[10px] text-neutral-400 shrink-0">
              <span>DSP Convolver & Feedback Delay</span>
              <span className="text-emerald-400 font-medium">Real-time Active</span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
